import { Card, Form, Button } from '@bancobarigui/barihaus';
import { message } from 'antd';

import { SignatureData, WizardMode } from '@/features/laudo/types/laudo-wizard.types';
import {
  evaluatorSignatureService,
  type SaveEvaluatorSignatureData,
} from '@/services/evaluatorSignature.service';

import { useSignatureData } from './useSignatureData';
import { EvaluatorForm } from './EvaluatorForm';
import { SignaturePreview } from './SignaturePreview';
import { SignatureUpload } from './SignatureUpload';
import { styles } from './styles';
import { ReviewFieldWrapper } from '../../ReviewFieldWrapper';

interface SignatureStepProps {
  data: SignatureData;
  onUpdate: (data: Partial<SignatureData>) => void;
  dataPreloaded?: boolean;
  mode?: WizardMode;
}

export function SignatureStep({ data, onUpdate, dataPreloaded = false, mode }: SignatureStepProps) {
  const [form] = Form.useForm();
  const isReviewMode = mode === 'review';
  const isCorrectionMode = mode === 'correction';

  const {
    loading,
    setLoading,
    signatureUrl,
    isDirty,
    updateSignatureImage,
    removeSignatureImage,
    updateFormData,
    markAsSaved,
  } = useSignatureData({ data, onUpdate, dataPreloaded, form });

  const handleSave = async () => {
    try {
      await form.validateFields();
      setLoading(true);

      const values = form.getFieldsValue();

      if (!data.signatureImage) {
        message.error('Please upload the signature');
        return;
      }

      const saveData: SaveEvaluatorSignatureData = {
        evaluatorName: values.evaluatorName,
        creaCauNumber: values.creaCauNumber,
        signatureBase64: data.signatureImage,
      };

      await evaluatorSignatureService.saveSignature(saveData);

      const savedData = {
        evaluatorName: saveData.evaluatorName,
        creaCauNumber: saveData.creaCauNumber || '',
        signatureImage: saveData.signatureBase64,
        _saved: true, // Marca como salvo para habilitar botão "Próximo"
      };

      markAsSaved(savedData);
      onUpdate(savedData); // Atualiza o estado global com a flag _saved

      message.success('Dados salvos com sucesso!');
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Por favor, preencha todos os campos obrigatórios');
      } else {
        message.error('Erro ao salvar os dados');
      }
    } finally {
      setLoading(false);
    }
  };

  const isSaveDisabled = !isDirty || !data.evaluatorName || !data.signatureImage;

  return (
    <div className="signature-step">

        <h4 style={styles.header}>Assinatura</h4>
        <p style={styles.description}>
          Preencha os dados do avaliador responsável pelo relatório. Esses dados serão salvos e
          reutilizados em relatórios futuros.
        </p>

        <EvaluatorForm
          form={form}
          data={data}
          onValuesChange={(_, allValues) => updateFormData(allValues)}
          disabled={isReviewMode}
          allowEditAllInCorrection={isCorrectionMode}
        >
          <ReviewFieldWrapper
            fieldPath="signature.signatureImage"
            fieldLabel="Assinatura Digital"
            stepName="Assinatura"
            allowEditAllInCorrection={isCorrectionMode}
          >
            <Form.Item label="Assinatura" required>
              {signatureUrl && <SignaturePreview signatureUrl={signatureUrl} />}

              {!isReviewMode && (
                <div style={styles.buttonContainer}>
                  <SignatureUpload
                    onUpload={updateSignatureImage}
                    onRemove={removeSignatureImage}
                    loading={loading}
                  />

                  <Button
                    buttonColor="primary"
                    onClick={handleSave}
                    loading={loading}
                    disabled={isSaveDisabled}
                  >
                    Salvar
                  </Button>
                </div>
              )}
            </Form.Item>
          </ReviewFieldWrapper>
        </EvaluatorForm>
    </div>
  );
}
